import { supabase } from '../lib/supabase';

/**
 * Decide where to send the user after a successful authentication.
 *
 * A profile is considered "set up" if the `users` row has at least one of
 * display_name, city, or favorite_categories valorized. Routing:
 *   - row exists + at least one field set → '/'         (returning user)
 *   - row exists + all fields empty       → '/profilo'  (first-time)
 *   - row missing (trigger may be pending) → '/profilo' (first-time)
 *   - query error after retry             → '/'         (lenient default;
 *       forcing every error to /profilo would trap returning users on desktop
 *       when the session cookie hasn't propagated to the select yet)
 */
export async function getPostAuthRedirect(userId: string): Promise<string> {
  const fetchRow = async () => {
    return supabase
      .from('users')
      .select('display_name, city, favorite_categories')
      .eq('id', userId)
      .maybeSingle();
  };

  let result;
  try {
    result = await fetchRow();
    if (result.error) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      result = await fetchRow();
    }
  } catch {
    return '/';
  }

  if (result.error) return '/';
  const data = result.data as {
    display_name: string | null;
    city: string | null;
    favorite_categories: string[] | null;
  } | null;
  if (!data) return '/profilo';

  const hasName = typeof data.display_name === 'string' && data.display_name.trim().length > 0;
  const hasCity = typeof data.city === 'string' && data.city.trim().length > 0;
  const hasCategories =
    Array.isArray(data.favorite_categories) && data.favorite_categories.length > 0;
  return hasName || hasCity || hasCategories ? '/' : '/profilo';
}
